import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GoGreenService, UserService } from '@api';
import { pollWhile } from '@app/core/functions/poll-while';
import { of, Subject, throwError } from 'rxjs';
import { catchError, takeUntil, switchMap, take, tap, shareReplay, finalize } from 'rxjs/operators';
import { WINDOW } from '@app/core/injection-tokens';
import { GoGreenStore } from './go-green-component-store';
import { NgxSpinnerService } from 'ngx-spinner';
import { UserAgentService } from '../services/user-agent.service';
import { GoGreenOptedInComponent } from './components/go-green-opted-in.component';
import { GoGreenOptOutComponent } from './components/go-green-opt-out.component';
import { GoGreenOptInComponent } from './components/go-green-opt-in.component';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-go-green',
  template: `
    @if (context$ | async; as context) {
      @if (context.info && !context.done) {
        @if (!context.info.electdocs) {
          <app-go-green-opt-in
            [info]="context.info"
            [intention]="context.intention"
            [verificationStatus]="context.verificationStatus"
            (sendVerificationEmail)="onSendVerificationEmail($event)"
            (optIn)="onOptIn($event)"
            (cancelVerification)="onCancelVerification()"
            (submitCorrectDetailsChanged)="onSubmitCorrectDetailsChanged($event)"
          ></app-go-green-opt-in>
        }
        @if (context.info.electdocs) {
          <app-go-green-opt-out (optOut)="onOptOut()"></app-go-green-opt-out>
        }
      }
      @if (context.done) {
        <app-go-green-opted-in></app-go-green-opted-in>
      }
    }
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GoGreenStore],
  imports: [GoGreenOptInComponent, GoGreenOptOutComponent, GoGreenOptedInComponent, AsyncPipe],
})
export class GoGreenContainer implements OnInit {
  private goGreenService = inject(GoGreenService);
  private userService = inject(UserService);
  private snackBar = inject(MatSnackBar);
  private window = inject<Window>(WINDOW);
  private goGreenStore = inject(GoGreenStore);
  private spinner = inject(NgxSpinnerService);
  private userAgentService = inject(UserAgentService);

  context$ = this.goGreenStore.state$;

  private verificationCanceled = new Subject<void>();

  ngOnInit(): void {
    void this.spinner.show();

    this.goGreenService
      .goGreenStatusRetrieve()
      .pipe(
        take(1),
        tap((result) => {
          this.goGreenStore.loadInfo(result);
        }),
        catchError(() => {
          this.snackBar.open('There was a problem. Please try again later');
          return of();
        }),
        finalize(() => void this.spinner.hide())
      )
      .subscribe();
  }

  controllingPage = '/loanmanager/go-green/';

  onSendVerificationEmail(payload: { emailAddress: string }) {
    this.goGreenStore.setVerificationWaiting();

    this.userService
      .userRequestEmailChangeCreate({ requestEmailChangeRequest: { email_address: payload.emailAddress } })
      .pipe(
        take(1),
        shareReplay(),
        switchMap((emailChangeResponse) =>
          emailChangeResponse.requires_validation
            ? this.userService.userIsValidatedRetrieve({ emailAddress: payload.emailAddress }).pipe(
                catchError((err: unknown) => {
                  console.error(err);
                  this.snackBar.open('There was a problem, please try again later');
                  return throwError(() => err);
                })
              )
            : of({ validated: true })
        ),
        pollWhile(5000, (res) => !res.validated, Infinity, true),
        takeUntil(this.verificationCanceled),
        tap((res) =>
          this.goGreenStore.setVerificationResult({
            email_address: payload.emailAddress,
            email_validated: res.validated,
          })
        ),
        catchError((err) => {
          console.error(err);
          return of();
        })
      )
      .subscribe();
  }

  onCancelVerification() {
    this.verificationCanceled.next();
    this.goGreenStore.setVerificationCanceled();
  }

  onOptIn(payload: { email: string }) {
    void this.spinner.show();

    const time_zone = this.userAgentService.getTimeZone();
    const browser = this.userAgentService.getBrowser();
    const os = this.userAgentService.getOS();
    const { email } = payload;

    this.goGreenService
      .goGreenOptInCreate({
        goGreenOptInRequest: {
          time_zone,
          browser,
          os,
          email,
        },
      })
      .pipe(
        take(1),
        tap(() => this.goGreenStore.setOptedIn()),
        catchError(() => {
          this.snackBar.open('There was a problem, please try again later');
          return of(null);
        }),
        finalize(() => {
          void this.spinner.hide();
        })
      )
      .subscribe();
  }

  onOptOut() {
    void this.spinner.show();

    this.goGreenService
      .goGreenOptOutCreate()
      .pipe(
        take(1),
        tap(() => {
          this.window.location.href = this.controllingPage;
        }),
        catchError(() => {
          this.snackBar.open('There was a problem, please try again later');
          return of(null);
        }),
        finalize(() => {
          void this.spinner.hide();
        })
      )
      .subscribe();
  }

  onSubmitCorrectDetailsChanged(value: boolean) {
    this.goGreenStore.setIntention(value);
  }
}
